import { z } from 'zod';
/**
 * The Knowledge module's value lists (`patterns/db-schema-conventions.md`, 5)
 * and the page-address rules both the backend and the dashboard apply
 * (`features/website-knowledge/technical.md`). The PostgreSQL enums are built
 * from these same arrays.
 */
/** Where a crawl is. `queued` and `running` are "in progress". */
export const CRAWL_STATUSES = ['queued', 'running', 'completed', 'failed'];
export const crawlStatusSchema = z.enum(CRAWL_STATUSES);
/** What started a crawl: the whole website, added addresses, or one page again. */
export const CRAWL_TYPES = ['website', 'urls', 'page_recrawl'];
export const crawlTypeSchema = z.enum(CRAWL_TYPES);
/** Why a page could not be captured (`patterns/error-handling.md`, 7). */
export const CRAWL_FAILURE_REASONS = [
    'unreachable',
    'timeout',
    'http_error',
    'not_html',
    'robots_disallowed',
    'too_large',
];
export const crawlFailureReasonSchema = z.enum(CRAWL_FAILURE_REASONS);
/**
 * What happened to one address the Account User added. `sitemap`: the address
 * was a sitemap, read for the page addresses it lists, which are each added
 * as their own results.
 */
export const IMPORT_OUTCOMES = [
    'added',
    'already_listed',
    'not_on_domains',
    'invalid',
    'unreadable',
    'sitemap',
];
export const importOutcomeSchema = z.enum(IMPORT_OUTCOMES);
/**
 * What a page is about, for retrieval: blogs rank below service pages, list
 * questions read the directory chunks, and a price answer gets the pricing
 * note. Set from the address and title first, else by the model at indexing.
 */
export const PAGE_TYPES = [
    'service',
    'team',
    'pricing',
    'faq',
    'contact',
    'policy',
    'blog',
    'other',
];
export const pageTypeSchema = z.enum(PAGE_TYPES);
/**
 * Where a knowledge chunk came from: a section of an approved page, a section
 * of a custom page, or a directory the indexer builds from every approved
 * page's entities (all services, the team, prices, contact).
 */
export const CHUNK_KINDS = ['page', 'custom_page', 'directory'];
export const chunkKindSchema = z.enum(CHUNK_KINDS);
/** The most addresses one "Add pages" request may carry. */
export const MAX_PAGE_ADDRESSES_PER_ADD = 50;
/** The most page addresses one "Add pages" request adds from the sitemaps in it. */
export const MAX_SITEMAP_PAGES_PER_ADD = 500;
/**
 * Whether an added address is worth reading as a sitemap first: it ends in
 * `.xml` or names a sitemap. One that turns out not to be is added as a page.
 */
export function looksLikeSitemap(url) {
    try {
        const path = new URL(url).pathname.toLowerCase();
        return path.endsWith('.xml') || path.includes('sitemap');
    }
    catch {
        return false;
    }
}
/**
 * Whether `host` is one of the website's domains. A domain also covers its
 * `www.` form, because a website set up as `clinic.example` is usually served
 * from `www.clinic.example` too, and the Account User means the same website.
 */
export function isHostOnDomains(host, domains) {
    const normalized = host.toLowerCase().replace(/\.$/, '');
    return domains.some((domain) => normalized === domain || normalized === `www.${domain}`);
}
/**
 * Normalizes a page address so the same page is listed once: `http`/`https`
 * only, lowercase host, no fragment, no default port, and no trailing slash
 * except for the root path. Returns `null` when the input isn't an absolute
 * web address.
 */
export function normalizePageUrl(input) {
    const trimmed = input.trim();
    if (trimmed.length === 0 || /\s/.test(trimmed))
        return null;
    let url;
    try {
        url = new URL(trimmed);
    }
    catch {
        return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:')
        return null;
    if (url.username !== '' || url.password !== '')
        return null;
    if (url.hostname === '')
        return null;
    url.hash = '';
    if (url.pathname.length > 1 && url.pathname.endsWith('/')) {
        url.pathname = url.pathname.replace(/\/+$/, '');
    }
    return url.toString();
}
/**
 * Checks one address the Account User typed: it must be a valid web address
 * on one of the website's domains (`features/website-knowledge/spec.md`, UC2).
 */
export function checkPageAddress(input, domains) {
    const url = normalizePageUrl(input);
    if (url === null)
        return { ok: false, outcome: 'invalid' };
    if (!isHostOnDomains(new URL(url).hostname, domains)) {
        return { ok: false, outcome: 'not_on_domains' };
    }
    return { ok: true, url };
}
/** The words the dashboard shows for each failure reason. */
export const CRAWL_FAILURE_REASON_TEXT = {
    unreachable: "Groundly couldn't reach this page.",
    timeout: 'The page took too long to respond.',
    http_error: 'The page returned an error.',
    not_html: "This address isn't a web page.",
    robots_disallowed: "Groundly isn't allowed to read this page.",
    too_large: 'The page is too large to capture.',
};
//# sourceMappingURL=knowledge.js.map